import React from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import {
  SquaresFour, ShieldCheck, MagnifyingGlass, FlowArrow, Books, FileText,
  CheckSquare, Buildings, Medal, Truck, Package, ClipboardText,
  Question, ChartLine, Gear, SignOut, Crosshair, ListMagnifyingGlass, Cube
} from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";

const NAV = [
  { to: "/", label: "Command Dashboard", icon: SquaresFour, end: true },
  { to: "/opportunities", label: "Opportunities", icon: Crosshair },
  { to: "/candidates", label: "Candidate Opportunities", icon: ListMagnifyingGlass },
  { to: "/discovery", label: "Tender Discovery Control", icon: MagnifyingGlass },
  { to: "/search-rules", label: "Search Rules", icon: FlowArrow },
  { to: "/search-intelligence", label: "Search Intelligence", icon: Books },
  { to: "/search-runs", label: "Search Runs", icon: ChartLine },
  { to: "/tasks", label: "Tasks", icon: CheckSquare },
  { to: "/documents", label: "Documents", icon: FileText },
  { to: "/buyers", label: "Buyers", icon: Buildings },
  { to: "/requirements", label: "Requirements", icon: ClipboardText },
  { to: "/awards", label: "Awards", icon: Medal },
  { to: "/suppliers", label: "Suppliers", icon: Truck },
  { to: "/supplier-assignments", label: "Supplier Assignments", icon: ShieldCheck },
  { to: "/supplier-samples", label: "Supplier Samples", icon: Package },
  { to: "/supplier-deliverables", label: "Supplier Deliverables", icon: FileText },
  { to: "/supplier-production", label: "Supplier Production", icon: Cube },
  { to: "/supplier-questions", label: "Supplier Questions", icon: Question },
  { to: "/reports", label: "Reports / Metrics", icon: ChartLine },
  { to: "/settings", label: "Settings", icon: Gear },
];

export default function Sidebar() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const isSupplier = user?.role === "supplier";

  if (isSupplier) {
    return (
      <aside className="w-[240px] shrink-0 border-r border-white/10 bg-[#050505] flex flex-col h-screen sticky top-0">
        <BrandHeader />
        <nav className="flex-1 overflow-y-auto py-2 text-sm">
          <NavItem to="/supplier" icon={SquaresFour} label="Supplier Portal" end />
        </nav>
        <UserFooter user={user} onLogout={async () => { await logout(); nav("/login"); }} />
      </aside>
    );
  }

  return (
    <aside className="w-[240px] shrink-0 border-r border-white/10 bg-[#050505] flex flex-col h-screen sticky top-0">
      <BrandHeader />
      <nav className="flex-1 overflow-y-auto py-2 text-sm" data-testid="sidebar-nav">
        {NAV.map(({ to, label, icon: Icon, end }) => (
          <NavItem key={to} to={to} icon={Icon} label={label} end={end} />
        ))}
      </nav>
      <UserFooter user={user} onLogout={async () => { await logout(); nav("/login"); }} />
    </aside>
  );
}

function BrandHeader() {
  return (
    <Link to="/" className="px-5 py-5 border-b border-white/10 block hover:bg-white/[0.02]">
      <div className="font-heading font-bold text-xl tracking-tighter text-white">
        <span className="text-[#FFB300]">+++</span>
      </div>
      <div className="text-[10px] tracking-[0.25em] uppercase text-zinc-500 mt-1">
        Tender Command Center
      </div>
    </Link>
  );
}

function NavItem({ to, icon: Icon, label, end }) {
  const testid = `sidebar-nav-${to.replace(/^\//, "").replace(/\//g, "-") || "dashboard"}`;
  return (
    <NavLink
      to={to}
      end={end}
      data-testid={testid}
      className={({ isActive }) =>
        `flex items-center gap-2.5 px-5 py-2 border-l-2 transition-none ${
          isActive
            ? "border-[#FFB300] bg-white/[0.04] text-white"
            : "border-transparent text-zinc-400 hover:text-white hover:bg-white/[0.02]"
        }`
      }
    >
      <Icon size={15} weight="regular" />
      <span className="text-[12.5px] tracking-tight">{label}</span>
    </NavLink>
  );
}

function UserFooter({ user, onLogout }) {
  return (
    <div className="border-t border-white/10 px-5 py-3 text-xs">
      {user && (
        <>
          <div className="text-white truncate" data-testid="sidebar-user-name">{user.name}</div>
          <div className="text-zinc-500 text-[10px] tracking-widest uppercase mt-0.5">{user.role.replace(/_/g, " ")}</div>
          <button
            onClick={onLogout}
            data-testid="sidebar-logout-btn"
            className="mt-2 flex items-center gap-1.5 text-zinc-400 hover:text-[#FFB300]"
          >
            <SignOut size={14} /> Sign out
          </button>
        </>
      )}
    </div>
  );
}
